import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { EntityImage } from '../components/EntityImage';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, X, Upload, Target, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { MAX_IMAGES_PER_ITEM, canAddMoreImages, getImageCountDisplay, validateImageUpload } from '../lib/imageLimit';

const FIREARM_TYPES = ['Pistol', 'Rifle', 'Shotgun', 'Other'];
const COMMON_CALIBERS = ['9mm', '.45 ACP', '.22 LR', '5.56 NATO', '.308 WIN', '12 Gauge', '.40 S&W', '.380 ACP', '7.62x39', '.223 REM'];
const CONDITIONS = ['New', 'Excellent', 'Good', 'Fair', 'Poor'];
const ACTIONS = ['Semi-automatic', 'Bolt-action', 'Pump-action', 'Lever-action', 'Break-action', 'Revolver', 'Single-shot'];
const COMMON_TAGS = ['EDC', 'Range', 'Home Defense', 'Collector', 'Hunting', 'Competition'];

export default function FirearmFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    manufacturer: '',
    model: '',
    type: '',
    caliber: '',
    serial_number: '',
    barrel_length: '',
    action: '',
    capacity: '',
    purchase_date: '',
    purchase_price: '',
    current_value: '',
    sale_date: '',
    sale_price: '',
    condition: '',
    notes: '',
    tags: [],
    images: [],
  });
  const [newTag, setNewTag] = useState('');
  const [customCaliber, setCustomCaliber] = useState('');

  useEffect(() => {
    if (isEdit) {
      const fetchFirearm = async () => {
        try {
          const response = await api.get(`/firearms/${id}`);
          const data = response.data;
          setFormData({
            ...data,
            capacity: data.capacity?.toString() || '',
            purchase_price: data.purchase_price?.toString() || '',
            current_value: data.current_value?.toString() || '',
            sale_price: data.sale_price?.toString() || '',
            sale_date: data.sale_date || '',
          });
        } catch (error) {
          toast.error('Firearm not found');
          navigate('/firearms');
        } finally {
          setLoading(false);
        }
      };
      fetchFirearm();
    }
  }, [id, isEdit, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    if (value === 'custom') return;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const { acceptedFiles, message } = validateImageUpload(files, formData.images.length);
    
    if (message) {
      toast.error(message);
    }
    
    if (acceptedFiles.length === 0) {
      // Reset input so user can try again
      e.target.value = '';
      return;
    }
    
    acceptedFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, reader.result],
        }));
      };
      reader.readAsDataURL(file);
    });
    
    // Reset input
    e.target.value = '';
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const addTag = (tag) => {
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag],
      }));
    }
    setNewTag('');
  };

  const removeTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.manufacturer || !formData.model || !formData.type || !formData.caliber) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        capacity: formData.capacity ? parseInt(formData.capacity) : null,
        purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : null,
        current_value: formData.current_value ? parseFloat(formData.current_value) : null,
        sale_price: formData.sale_price ? parseFloat(formData.sale_price) : null,
        sale_date: formData.sale_date || null,
      };

      if (isEdit) {
        await api.put(`/firearms/${id}`, payload);
        toast.success('Firearm updated');
      } else {
        await api.post('/firearms', payload);
        toast.success('Firearm added');
      }
      navigate('/firearms');
    } catch (error) {
      console.error('Error saving firearm:', error);
      toast.error('Failed to save firearm');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout title={isEdit ? 'Edit Firearm' : 'Add Firearm'}>
        <div className="animate-pulse space-y-6 max-w-2xl">
          <div className="h-10 bg-muted rounded w-full" />
          <div className="h-10 bg-muted rounded w-full" />
          <div className="h-10 bg-muted rounded w-full" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={isEdit ? 'Edit Firearm' : 'Add Firearm'}>
      <div className="max-w-2xl" data-testid="firearm-form-page">
        <Button
          variant="ghost"
          onClick={() => navigate('/firearms')}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Firearms
        </Button>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <div className="bg-card border border-border rounded-sm p-6 space-y-4">
            <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
              Basic Information
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="manufacturer">Manufacturer *</Label>
                <Input
                  id="manufacturer"
                  name="manufacturer"
                  value={formData.manufacturer}
                  onChange={handleInputChange}
                  placeholder="e.g., Glock, Smith & Wesson"
                  className="rounded-sm"
                  required
                  data-testid="input-manufacturer"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="model">Model *</Label>
                <Input
                  id="model"
                  name="model"
                  value={formData.model}
                  onChange={handleInputChange}
                  placeholder="e.g., G19 Gen 5"
                  className="rounded-sm"
                  required
                  data-testid="input-model"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type">Type *</Label>
                <Select value={formData.type} onValueChange={(v) => handleSelectChange('type', v)}>
                  <SelectTrigger className="rounded-sm" data-testid="select-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {FIREARM_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="caliber">Caliber *</Label>
                <Select value={COMMON_CALIBERS.includes(formData.caliber) ? formData.caliber : ''} onValueChange={(v) => handleSelectChange('caliber', v)}>
                  <SelectTrigger className="rounded-sm" data-testid="select-caliber">
                    <SelectValue placeholder="Select caliber" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMMON_CALIBERS.map(caliber => (
                      <SelectItem key={caliber} value={caliber}>{caliber}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!COMMON_CALIBERS.includes(formData.caliber) && formData.caliber && (
                  <div className="flex items-center gap-2 p-2 bg-secondary/10 rounded-sm mt-2">
                    <span className="text-sm font-medium">Custom: {formData.caliber}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormData(prev => ({ ...prev, caliber: '' }))}
                      className="h-6 px-2 text-xs"
                    >
                      Clear
                    </Button>
                  </div>
                )}
                <div className="flex gap-2 mt-2">
                  <Input
                    placeholder="Or enter custom caliber"
                    value={customCaliber}
                    onChange={(e) => setCustomCaliber(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (customCaliber.trim()) {
                          setFormData(prev => ({ ...prev, caliber: customCaliber.trim() }));
                          setCustomCaliber('');
                        }
                      }
                    }}
                    className="rounded-sm flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      if (customCaliber.trim()) {
                        setFormData(prev => ({ ...prev, caliber: customCaliber.trim() }));
                        setCustomCaliber('');
                      }
                    }}
                    disabled={!customCaliber.trim()}
                    className="rounded-sm"
                  >
                    Set
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Specifications */}
          <div className="bg-card border border-border rounded-sm p-6 space-y-4">
            <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
              Specifications
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="action">Action</Label>
                <Select value={formData.action || ''} onValueChange={(v) => handleSelectChange('action', v)}>
                  <SelectTrigger className="rounded-sm" data-testid="select-action">
                    <SelectValue placeholder="Select action" />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTIONS.map(action => (
                      <SelectItem key={action} value={action}>{action}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="barrel_length">Barrel Length</Label>
                <Input
                  id="barrel_length"
                  name="barrel_length"
                  value={formData.barrel_length}
                  onChange={handleInputChange}
                  placeholder='e.g., 4.02"'
                  className="rounded-sm"
                  data-testid="input-barrel-length"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="capacity">Capacity (rounds)</Label>
                <Input
                  id="capacity"
                  name="capacity"
                  type="number"
                  value={formData.capacity}
                  onChange={handleInputChange}
                  placeholder="e.g., 15"
                  className="rounded-sm"
                  data-testid="input-capacity"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="condition">Condition</Label>
                <Select value={formData.condition || ''} onValueChange={(v) => handleSelectChange('condition', v)}>
                  <SelectTrigger className="rounded-sm" data-testid="select-condition">
                    <SelectValue placeholder="Select condition" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONDITIONS.map(condition => (
                      <SelectItem key={condition} value={condition}>{condition}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="serial_number">Serial Number</Label>
              <Input
                id="serial_number"
                name="serial_number"
                value={formData.serial_number}
                onChange={handleInputChange}
                placeholder="Enter serial number"
                className="rounded-sm font-mono"
                data-testid="input-serial"
              />
              <p className="text-xs text-muted-foreground">This is stored securely and can be hidden from view.</p>
            </div>
          </div>

          {/* Purchase Info */}
          <div className="bg-card border border-border rounded-sm p-6 space-y-4">
            <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
              Purchase Information
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="purchase_date">Purchase Date</Label>
                <Input
                  id="purchase_date"
                  name="purchase_date"
                  type="date"
                  value={formData.purchase_date}
                  onChange={handleInputChange}
                  className="rounded-sm"
                  data-testid="input-purchase-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="purchase_price">Purchase Price ($)</Label>
                <Input
                  id="purchase_price"
                  name="purchase_price"
                  type="number"
                  step="0.01"
                  value={formData.purchase_price}
                  onChange={handleInputChange}
                  placeholder="e.g., 599.99"
                  className="rounded-sm"
                  data-testid="input-purchase-price"
                />
              </div>
            </div>
          </div>

          {/* Current Value */}
          <div className="bg-card border border-border rounded-sm p-6 space-y-4">
            <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
              Current Value
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Enter the estimated current market value to track appreciation or depreciation.
            </p>
            <div className="space-y-2">
              <Label htmlFor="current_value">Estimated Current Value ($)</Label>
              <Input
                id="current_value"
                name="current_value"
                type="number"
                step="0.01"
                value={formData.current_value}
                onChange={handleInputChange}
                placeholder="e.g., 650.00"
                className="rounded-sm"
                data-testid="input-current-value"
              />
            </div>
            {formData.purchase_price && formData.current_value && (
              <div className="mt-4 p-3 bg-muted/50 rounded-sm">
                {(() => {
                  const purchase = parseFloat(formData.purchase_price);
                  const current = parseFloat(formData.current_value);
                  const diff = current - purchase;
                  const pct = ((diff / purchase) * 100).toFixed(1);
                  const isGain = diff > 0;
                  return (
                    <p className={`text-sm font-medium ${isGain ? 'text-green-500' : diff < 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
                      {isGain ? '↑' : diff < 0 ? '↓' : '→'} {isGain ? '+' : ''}{diff < 0 ? '-' : ''}${Math.abs(diff).toLocaleString()} ({isGain ? '+' : ''}{pct}%)
                    </p>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Sale Information */}
          <div className="bg-card border border-border rounded-sm p-6 space-y-4">
            <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
              Sale Information
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Record sale details when you sell this firearm.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sale_date">Sale Date</Label>
                <Input
                  id="sale_date"
                  name="sale_date"
                  type="date"
                  value={formData.sale_date}
                  onChange={handleInputChange}
                  className="rounded-sm"
                  data-testid="input-sale-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sale_price">Sale Price ($)</Label>
                <Input
                  id="sale_price"
                  name="sale_price"
                  type="number"
                  step="0.01"
                  value={formData.sale_price}
                  onChange={handleInputChange}
                  placeholder="e.g., 700.00"
                  className="rounded-sm"
                  data-testid="input-sale-price"
                />
              </div>
            </div>
            {formData.purchase_price && formData.sale_price && (
              <div className="mt-4 p-3 bg-muted/50 rounded-sm">
                {(() => {
                  const purchase = parseFloat(formData.purchase_price);
                  const sale = parseFloat(formData.sale_price);
                  const diff = sale - purchase;
                  const pct = ((diff / purchase) * 100).toFixed(1);
                  const isGain = diff > 0;
                  return (
                    <p className={`text-sm font-medium ${isGain ? 'text-green-500' : diff < 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
                      {isGain ? '↑ Profit:' : diff < 0 ? '↓ Loss:' : '→ Break Even:'} {isGain ? '+' : ''}{diff < 0 ? '-' : ''}${Math.abs(diff).toLocaleString()} ({isGain ? '+' : ''}{pct}%)
                    </p>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Tags */}
          <div className="bg-card border border-border rounded-sm p-6 space-y-4">
            <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
              Tags
            </h3>

            <div className="flex flex-wrap gap-2 mb-4">
              {formData.tags.map(tag => (
                <Badge key={tag} variant="outline" className="rounded-sm gap-1">
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)}>
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              {COMMON_TAGS.filter(tag => !formData.tags.includes(tag)).map(tag => (
                <Button
                  key={tag}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addTag(tag)}
                  className="rounded-sm"
                >
                  + {tag}
                </Button>
              ))}
            </div>

            <div className="flex gap-2 mt-4">
              <Input
                placeholder="Add custom tag"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag(newTag))}
                className="rounded-sm"
                data-testid="input-custom-tag"
              />
              <Button type="button" variant="outline" onClick={() => addTag(newTag)} className="rounded-sm">
                Add
              </Button>
            </div>
          </div>

          {/* Images */}
          <div className="bg-card border border-border rounded-sm p-6 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo']">
                Images
              </h3>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <ImageIcon className="w-3 h-3" />
                {getImageCountDisplay(formData.images.length)}
              </span>
            </div>

            <div className="grid grid-cols-4 gap-4">
              {formData.images.map((img, idx) => (
                <div key={idx} className="relative aspect-square bg-muted rounded-sm overflow-hidden group">
                  <EntityImage fileId={img} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImage(idx)}
                    className="absolute top-2 right-2 p-1 bg-destructive text-destructive-foreground rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {canAddMoreImages(formData.images.length) && (
                <label className="aspect-square border-2 border-dashed border-border rounded-sm flex flex-col items-center justify-center cursor-pointer hover:border-secondary transition-colors">
                  <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                  <span className="text-xs text-muted-foreground">Upload</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="input-images"
                  />
                </label>
              )}
            </div>
            {formData.images.length >= MAX_IMAGES_PER_ITEM && (
              <p className="text-xs text-muted-foreground">
                Maximum {MAX_IMAGES_PER_ITEM} photos reached. Remove existing photos to add new ones.
              </p>
            )}
          </div>

          {/* Notes */}
          <div className="bg-card border border-border rounded-sm p-6 space-y-4">
            <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
              Notes
            </h3>
            <Textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              placeholder="Add any additional notes..."
              rows={4}
              className="rounded-sm"
              data-testid="input-notes"
            />
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <Button
              type="submit"
              disabled={saving}
              className="bg-secondary hover:bg-secondary/90 rounded-sm btn-tactical"
              data-testid="submit-btn"
            >
              {saving ? 'Saving...' : isEdit ? 'Update Firearm' : 'Add Firearm'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/firearms')}
              className="rounded-sm"
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
